import React from 'react';
import { TextShell } from '@qover/atoms/src';
import { Container, BikeIconContainer } from './bikeLabels.styles';
import { messagesPerVariant } from '../bike.messages';
import { Variant } from '../domain/Terms';
import { useLocation, useHistory } from 'react-router-dom';

const CheckmarkIcon = () => (
  <svg width="18" height="18" viewBox="0 0 30 30">
    <path
      d="M7.92 12.74l-.82-.82a2.4 2.4 0 10-3.4 3.4l.81.81 6.5 6.56a2.4 2.4 0 003.4 0l11.9-12a2.4 2.4 0 00-3.42-3.38L12.71 17.58l-4.8-4.84z"
      fillRule="nonzero"
    />
  </svg>
);

const CrossmarkIcon = () => (
  <svg width="18" height="18" viewBox="0 0 30 30">
    <path
      d="M24.7 5.3a2.74 2.74 0 010 3.88L18.88 15l5.82 5.82a2.74 2.74 0 01.13 3.74l-.13.14a2.74 2.74 0 01-3.74.13l-.14-.13L15 18.88 9.18 24.7a2.74 2.74 0 11-3.88-3.88L11.12 15 5.3 9.18a2.74 2.74 0 01-.13-3.74l.13-.14a2.74 2.74 0 013.74-.13l.14.13L15 11.12l5.82-5.82a2.74 2.74 0 013.88 0z"
      fillRule="nonzero"
    />
  </svg>
);

type BikeLabelProps = {
  variant: Variant;
  id: string;
  status: 'hidden' | 'included' | 'excluded';
};

type BikeIconLabelProps = {
  variant: Variant;
  id: string;
};

const BikeIconLabel = (props: BikeIconLabelProps) => (
  <BikeIconContainer icon={messagesPerVariant[props.variant].optionIcon}>
    <span>{messagesPerVariant[props.variant].optionIconLabel}</span>
  </BikeIconContainer>
);

const BikeLabel = (props: BikeLabelProps) => {
  const location = useLocation();
  const history = useHistory();

  return (
    <Container className={props.status}>
      {['included', 'hidden'].includes(props.status) && <CheckmarkIcon />}
      {props.status === 'excluded' && <CrossmarkIcon />}
      <span
        onClick={() => {
          history.push({ ...location, hash: '#insurance-details' });
          const accordion = document.getElementById('insurance-details');
          if (accordion) {
            accordion.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }}
      >
        <TextShell size="lg">
          {messagesPerVariant[props.variant].optionLabel}
        </TextShell>
      </span>
    </Container>
  );
};

const variants = [
  Variant.VARIANT_THEFT,
  Variant.VARIANT_THEFT_ASSISTANCE,
  Variant.VARIANT_THEFT_DAMAGE_ASSISTANCE,
];

export const BikeLabels = {
  [Variant.VARIANT_THEFT]: () => (
    <>
      {[Variant.VARIANT_THEFT, Variant.VARIANT_THEFT_DAMAGE].map(
        (variant, idx) => (
          <BikeLabel
            id="label-option-theft"
            status={idx <= 0 ? 'included' : 'excluded'}
            key={`${idx}-${variant}-label-option-theft`}
            variant={variant}
          />
        ),
      )}
    </>
  ),
  [Variant.VARIANT_THEFT_ASSISTANCE]: () => (
    <>
      {variants.map((variant, idx) => (
        <BikeLabel
          id="label-option-theft-assistance"
          status={idx <= 1 ? 'included' : 'excluded'}
          key={`${idx}-${variant}-label-option-theft-assistance`}
          variant={variant}
        />
      ))}
    </>
  ),
  [Variant.VARIANT_THEFT_DAMAGE_ASSISTANCE]: () => (
    <>
      {variants.map((variant, idx) => (
        <BikeLabel
          id="label-option-theft-damage-assistance"
          status={idx <= 2 ? 'included' : 'excluded'}
          key={`${idx}-${variant}-label-option-theft-damage-assistance`}
          variant={variant}
        />
      ))}
    </>
  ),
  [Variant.VARIANT_ASSISTANCE]: () => null,
  [Variant.VARIANT_THEFT_DAMAGE]: () => (
    <>
      {[Variant.VARIANT_THEFT, Variant.VARIANT_THEFT_DAMAGE].map(
        (variant, idx) => (
          <BikeLabel
            id="label-option-theft-damage"
            status={idx <= 1 ? 'included' : 'excluded'}
            key={`${idx}-${variant}-label-option-theft-damage`}
            variant={variant}
          />
        ),
      )}
    </>
  ),
  [Variant.VARIANT_UNSPECIFIED]: () => (
    <>
      {[Variant.VARIANT_UNSPECIFIED, Variant.VARIANT_UNSPECIFIED].map(
        (variant, idx) => (
          <BikeLabel
            id="label-option-theft-damage"
            status="hidden"
            key={`${idx}-${variant}-label-option-theft-damage`}
            variant={variant}
          />
        ),
      )}
    </>
  ),
};

export const BikesIconsLabels = {
  [Variant.VARIANT_THEFT]: () =>
    [Variant.VARIANT_THEFT, Variant.VARIANT_THEFT_DAMAGE]
      .slice(0, 1)
      .map((variant, idx) => (
        <BikeIconLabel
          id="label-option-theft"
          key={`${idx}-${variant}-label-option-theft`}
          variant={variant}
        />
      )),
  [Variant.VARIANT_THEFT_ASSISTANCE]: () =>
    variants
      .slice(0, 2)
      .map((variant, idx) => (
        <BikeIconLabel
          id="label-option-theft-assistance"
          key={`${idx}-${variant}-label-option-theft-assistance`}
          variant={variant}
        />
      )),
  [Variant.VARIANT_THEFT_DAMAGE_ASSISTANCE]: () =>
    variants
      .slice(0, 3)
      .map((variant, idx) => (
        <BikeIconLabel
          id="label-option-theft-damage-assistance"
          key={`${idx}-${variant}-label-option-theft-damage-assistance`}
          variant={variant}
        />
      )),
  [Variant.VARIANT_ASSISTANCE]: () => null,
  [Variant.VARIANT_THEFT_DAMAGE]: () =>
    [Variant.VARIANT_THEFT, Variant.VARIANT_THEFT_DAMAGE]
      .slice(0, 2)
      .map((variant, idx) => (
        <BikeIconLabel
          id="label-option-theft-damage"
          key={`${idx}-${variant}-label-option-theft-damage`}
          variant={variant}
        />
      )),
  [Variant.VARIANT_UNSPECIFIED]: () => null,
};
